import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogActions, Button, Alert,
  TextField, MenuItem, CircularProgress,
} from "@mui/material";
import axios from "../../../config/axiosConfig";
import { Encabezado, Seccion, Rejilla, fieldSx, contenidoSx, pieSx, botonPrincipalSx } from "./dialogoUi";

const PROCEDENCIA_CI = ["LP", "CB", "SC", "BE", "PA", "TJ", "PT", "CH", "OR"];
const DEPARTAMENTOS = ["La Paz", "Oruro", "Cochabamba", "Santa Cruz", "Beni", "Pando", "Tarija", "Potosi", "Chuquisaca"];
const EMPRESAS = ["TRS Ltda", "Chasqui Ltda"];
const TITULOS = ["Señor", "Señora", "Licenciado", "Licenciada", "Ingeniero", "Ingeniera", "Doctor", "Doctora", "Tecnico Medio", "Tecnico Superior", "Estudiante"];

const hoy = () => new Date().toISOString().slice(0, 10);

const initialForm = {
  nombre: "", paterno: "", materno: "", ci: "", procedencia_ci: "LP", titulo: TITULOS[0],
  correo: "", telefono: "", celular: "", direccion: "", departamento: "La Paz",
  fecha_cumple: hoy(), empresa: EMPRESAS[0], fecha_pas: "", fecha_con: "", fecha_afi: "",
  cargo: "", contacto: "", observaciones: "",
  nro_cuenta: "", caja: "", codigo_asegurado: "", nua: "",
};

// "Adicionar Personal" (per_adicionar_personal): NO registra contrato — eso queda
// como "Registrar Contrato" en la ficha del empleado nuevo (Próximamente, no
// compartido), igual que el legacy redirige a per_registro_contrato después.
const AdicionarPersonalDialog = ({ open, onClose, onCreado }) => {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(initialForm);
      setError("");
    }
  }, [open]);

  const handleChange = (campo) => (e) => setForm((prev) => ({ ...prev, [campo]: e.target.value }));

  const puedeGuardar = form.nombre && form.paterno && form.materno && form.departamento && form.empresa && !saving;

  const handleGuardar = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.post("/api/administracion-personal/empleado", form);
      onCreado(data.id);
    } catch (e) {
      setError(e?.response?.data?.message || "Error al crear el empleado.");
    } finally {
      setSaving(false);
    }
  };

  const campo = (clave, label, extra = {}) => (
    <TextField size="small" label={label} value={form[clave]} onChange={handleChange(clave)} sx={fieldSx} {...extra} />
  );
  const fecha = (clave, label) => campo(clave, label, { type: "date", InputLabelProps: { shrink: true } });

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth scroll="paper">
      <Encabezado titulo="Adicionar personal" subtitulo="El contrato se registra después, desde la ficha del empleado" />
      <DialogContent sx={contenidoSx}>
        {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}

        <Seccion titulo="Datos personales">
          <Rejilla>
            {campo("nombre", "Nombre", { required: true })}
            {campo("paterno", "Ap. paterno", { required: true })}
            {campo("materno", "Ap. materno", { required: true })}
            {campo("ci", "Carnet de identidad")}
            <TextField select size="small" label="Procedencia CI" value={form.procedencia_ci} onChange={handleChange("procedencia_ci")} sx={fieldSx}>
              {PROCEDENCIA_CI.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Título" value={form.titulo} onChange={handleChange("titulo")} sx={fieldSx}>
              {TITULOS.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            {fecha("fecha_cumple", "Fecha de nacimiento")}
          </Rejilla>
        </Seccion>

        <Seccion titulo="Contacto">
          <Rejilla>
            {campo("correo", "Correo electrónico")}
            {campo("celular", "Celular")}
            {campo("telefono", "Teléfono")}
            {campo("direccion", "Dirección", { sx: { ...fieldSx, gridColumn: { sm: "span 2" } } })}
            <TextField select size="small" label="Departamento" required value={form.departamento} onChange={handleChange("departamento")} sx={fieldSx}>
              {DEPARTAMENTOS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </TextField>
          </Rejilla>
        </Seccion>

        <Seccion titulo="Datos laborales">
          <Rejilla>
            <TextField select size="small" label="Empresa" required value={form.empresa} onChange={handleChange("empresa")} sx={fieldSx}>
              {EMPRESAS.map((e) => <MenuItem key={e} value={e}>{e}</MenuItem>)}
            </TextField>
            {campo("cargo", "Cargo")}
            {fecha("fecha_pas", "Inicio de pasantía")}
            {fecha("fecha_con", "Fecha de contrato")}
            {fecha("fecha_afi", "Fecha de afiliación")}
          </Rejilla>
        </Seccion>

        <Seccion titulo="Cuenta y seguro">
          <Rejilla cols={4}>
            {campo("nro_cuenta", "Nro. de cuenta")}
            {campo("caja", "Caja")}
            {campo("codigo_asegurado", "Código del asegurado")}
            {campo("nua", "NUA")}
          </Rejilla>
        </Seccion>

        <Seccion titulo="Notas">
          <Rejilla cols={1}>
            {campo("contacto", "Persona de contacto", { multiline: true, minRows: 2 })}
            {campo("observaciones", "Observaciones", { multiline: true, minRows: 2 })}
          </Rejilla>
        </Seccion>
      </DialogContent>
      <DialogActions sx={pieSx}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>Cancelar</Button>
        <Button variant="contained" onClick={handleGuardar} disabled={!puedeGuardar} sx={botonPrincipalSx}>
          {saving ? <CircularProgress size={20} color="inherit" /> : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdicionarPersonalDialog;
